import React, { useEffect, useRef, useState } from 'react'
import { getApiUrl } from '@/config/runtime-env'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { startImpersonatedSession } from '@/utils/tokenService'

/**
 * Landing page for an agency-admin impersonation link (/impersonate?code=…).
 * Swaps the one-time code for a short-lived token, keeps it in this tab's
 * sessionStorage only, then does a full reload so the app boots as that user.
 */
const Impersonate: React.FC = () => {
  const [error, setError] = useState<string | null>(null)
  // The code is single-use: guard against React strict-mode double effects.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const code = new URLSearchParams(window.location.search).get('code')
    if (!code) {
      setError('This impersonation link is missing its code.')
      return
    }

    ;(async () => {
      try {
        const response = await fetch(`${getApiUrl()}/impersonation/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        })
        const body = await response.json().catch(() => ({}))
        const data = body.data || body
        if (!response.ok || !data.accessToken) {
          throw new Error(data.message || body.message || 'This link is invalid, expired or already used.')
        }

        startImpersonatedSession(
          {
            sessionId: data.sessionId,
            targetName: data.user?.name ?? null,
            targetRole: data.user?.userType ?? 'agency_admin',
            actorName: data.impersonatedBy?.name ?? null,
            expiresAt: data.expiresAt,
          },
          data.accessToken,
        )
        // Strip the code from the URL/history, and boot fresh as the impersonated user.
        window.location.replace('/')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not start impersonation.')
      }
    })()
  }, [])

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      {error ? (
        <div className="max-w-sm rounded-lg border border-red-200 bg-white p-6 text-center shadow-sm">
          <p className="font-semibold text-gray-900">Impersonation failed</p>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <p className="mt-3 text-xs text-gray-400">
            Links work once and expire after about a minute. Start again from the admin portal.
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 text-sm text-gray-500">
          <LoadingSpinner size="lg" />
          Starting impersonated session…
        </div>
      )}
    </div>
  )
}

export default Impersonate
